import { AsyncFunction } from "vig-ui-library";
import { TaskModifiers, useTask, UseTaskReturn } from "vig-ui-library/src/hooks/useTask";
import httpStatusExceptionSchema from "@/schemas/HttpStatusExceptionSchema";
import { ErrorCode } from "@/enums/ErrorCode";

export type TaskWithErrorHandlingParams<P extends any[]> = {
    asyncFunction: AsyncFunction<P, void>;
    modifiersPartial?: Partial<TaskModifiers<void>>;
    genericErrorMessage: string;
    onError: (message: string, errorCode?: ErrorCode, errors?: Record<string, string> | null | undefined) => void;
}

export const extractErrorCode = (value?: string | null): ErrorCode | null => {
    if (!value) return null;
    if (!value.includes(".")) return null;
    const [_httpStatus, errorCode] = value.split(".");
    if (!errorCode) return null;
    var parsed = parseInt(errorCode, 10);
    if (isNaN(parsed)) return null;
    return parsed;
}

export const getErrorMessage = (errorCode: ErrorCode): string => {
    switch (errorCode) {
        case ErrorCode.MISSING_RECAPTCHA_CODE:
        case ErrorCode.RECAPTCHA_VERIFICATION_FAILED:
            return "reCAPTCHA overenie zlyhalo. Skúste to prosím znova.";
        case ErrorCode.MISSING_COMPANY_TYPE:
        case ErrorCode.PUBLIC_REQUEST_MISSING_TOKEN:
        case ErrorCode.PUBLIC_REQUEST_UNSUPPORTED_REQUEST_MASTER_TYPE:
        case ErrorCode.PUBLIC_REQUEST_MISSING_REQUEST_MASTER_TYPE:
            return "Nastala chyba počas spracovania požiadavky. Skúste to prosím neskôr.";
        case ErrorCode.PUBLIC_REQUEST_INVALID_FORM_VALUES:
            return "Zadané údaje vo formulári sú nesprávne alebo neúplné. Skontrolujte ich prosím a skúste to znova.";  
        case ErrorCode.PUBLIC_REQUEST_DOCUMENT_NOT_FOUND_OR_FORBIDDEN:
            return "Nastala chyba pri sťahovaní. Skúste to prosím neskôr."
        case ErrorCode.PUBLIC_REQUEST_INVALID_TOKEN:
        case ErrorCode.PUBLIC_REQUEST_MISMATCHED_TOKEN:
        case ErrorCode.PUBLIC_REQUEST_MISMATCHED_REQUEST_MASTER_TYPE:
        case ErrorCode.PUBLIC_REQUEST_INVALID_FORM_CONTRACT_OR_PROPOSAL_ID:
        case ErrorCode.PUBLIC_REQUEST_MISMATCHED_FORM_VALUES:
        case ErrorCode.PUBLIC_REQUEST_INVALID_TOKEN_METADATA_REQUEST_TYPE_ID:
        case ErrorCode.PUBLIC_REQUEST_INVALID_TOKEN_METADATA_CONTRACT_OR_PROPOSAL_ID:
        case ErrorCode.PUBLIC_REQUEST_INVALID_CONTRACT:
        case ErrorCode.PUBLIC_REQUEST_SAVE_FAILED:
        case ErrorCode.PUBLIC_REQUEST_SUBMIT_FAILED:
        case ErrorCode.PUBLIC_REQUEST_MARK_FAILED:
            return "PUBLIC_REQUEST_INVALID";    
    }
}

export const useTaskWithErrorHandling = <P extends any[]>(params: TaskWithErrorHandlingParams<P>): UseTaskReturn<P, void> => {
    const asyncFunction = async (...args: P): Promise<void> => {
        try {
            await params.asyncFunction(...args);
        } catch (error) {
            const httpError = httpStatusExceptionSchema.mapFromError(error);
            const errorCode = extractErrorCode(httpError.errorCode);
            const errors = httpError.errors;
            if (!!errorCode) {
                const errorMessage = getErrorMessage(errorCode);
                params.onError(errorMessage, errorCode, errors);
                return;
            }
            const genericErrorMessage = params.genericErrorMessage || "Nastala chyba. Skúste to prosím neskôr.";
            const message = httpStatusExceptionSchema.mergeErrorMessages(httpError, genericErrorMessage);
            params.onError(message);
        }
    }


    const task = useTask(asyncFunction, params.modifiersPartial);

    return task;
}
